import type { AnalyticsTimeframe, AnalyticsUnit } from 'common/dist/types/apiTypes';
import { ADALONG_COLORS } from '@src/styles';
import { useEffect, useState } from 'react';
import { classes, stylesheet } from 'typestyle';

export interface AnalyticsTimeframeConfig {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  marks?: number[];
}

export const ANALYTICS_TIMEFRAME_CONFIG: Record<AnalyticsUnit, AnalyticsTimeframeConfig> = {
  week: { min: 4, max: 52, step: 1, defaultValue: 12 },
  month: { min: 3, max: 24, step: 1, defaultValue: 12 },
  quarter: { min: 2, max: 12, step: 1, defaultValue: 4 },
  year: { min: 2, max: 5, step: 1, defaultValue: 3 },
};

export const ANALYTICS_UNITS: readonly AnalyticsUnit[] = ['week', 'month', 'quarter', 'year'];

const UNIT_LABELS: Record<AnalyticsUnit, string> = {
  week: 'Weeks',
  month: 'Months',
  quarter: 'Quarters',
  year: 'Years',
};

const UNIT_TAB_LABELS: Record<AnalyticsUnit, string> = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
};

interface Props {
  unit: AnalyticsUnit;
  timeframe: AnalyticsTimeframe;
  numericTimeframe?: number;
  onUnitChange: (unit: AnalyticsUnit) => void;
  onTimeframeChange: (timeframe: AnalyticsTimeframe) => void;
}

export function AnalyticsUnitTimeframePicker({
  unit,
  timeframe,
  numericTimeframe,
  onUnitChange,
  onTimeframeChange,
}: Props) {
  const config = ANALYTICS_TIMEFRAME_CONFIG[unit];
  const isAll = timeframe === 'all';
  const numericValue = isAll
    ? numericTimeframe ?? config.defaultValue
    : timeframe;
  const unitLabel = UNIT_LABELS[unit];

  // The slider keeps an uncommitted draft while the pointer is held (or a key
  // is being stepped) so a drag never refetches the embed or rewrites the URL
  // per pointer movement. The committed selection only changes on release:
  // pointerup / pointercancel (touch), keyup (keyboard) or blur (safety net).
  const [draft, setDraft] = useState<number | null>(null);
  const displayed = draft ?? numericValue;

  useEffect(() => {
    // The committed selection changed from outside (unit switch, URL
    // migration, back/forward): drop any in-progress draft.
    setDraft(null);
  }, [unit, timeframe, numericValue]);

  const commitDraft = () => {
    if (draft === null) {
      return;
    }
    setDraft(null);
    if (!(typeof timeframe === 'number' && timeframe === draft)) {
      onTimeframeChange(draft);
    }
  };

  // All is a toggle: while active, clicking it again restores the last
  // numeric timeframe (numericValue keeps the URL value or the unit default).
  const toggleAll = () => {
    if (isAll) {
      onTimeframeChange(numericValue);
    } else {
      onTimeframeChange('all');
    }
  };

  // The slider stays operable while All is active (only muted visually):
  // clicking it re-enables the range on the shown value.
  const clickSliderWhileAll = () => {
    if (!isAll) {
      return;
    }
    setDraft(null);
    onTimeframeChange(displayed);
  };

  return (
    <section className={sheet.controls} aria-label="Analytics period">
      <div className={sheet.controlGroup}>
        <span className={sheet.controlLabel}>Unit</span>
        <div className={sheet.unitTabs} role="tablist" aria-label="Analytics unit">
          {ANALYTICS_UNITS.map((candidate) => {
            const isActive = candidate === unit;
            return (
              <button
                key={candidate}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="analytics-period-controls"
                className={classes(sheet.unitTab, isActive && sheet.unitTabActive)}
                onClick={() => onUnitChange(candidate)}
              >
                {UNIT_TAB_LABELS[candidate]}
              </button>
            );
          })}
        </div>
      </div>

      <div id="analytics-period-controls" className={sheet.controlGroup}>
        <span className={sheet.controlLabel}>Timeframe</span>
        <div className={sheet.timeframeControl}>
          <label
            className={classes(sheet.rangeLabel, isAll && sheet.rangeMuted)}
            htmlFor="analytics-timeframe-range"
          >
            <span className={sheet.srOnly}>Last number of {unitLabel.toLowerCase()}</span>
            <input
              id="analytics-timeframe-range"
              type="range"
              min={config.min}
              max={config.max}
              step={config.step}
              value={displayed}
              aria-label={`Timeframe in ${unitLabel.toLowerCase()}`}
              aria-valuetext={`Last ${displayed} ${unitLabel.toLowerCase()}`}
              onClick={clickSliderWhileAll}
              onChange={(event) => setDraft(Number(event.target.value))}
              onPointerUp={commitDraft}
              onPointerCancel={commitDraft}
              onKeyUp={commitDraft}
              onBlur={commitDraft}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setDraft(null);
                }
              }}
            />
            <output
              htmlFor="analytics-timeframe-range"
              className={classes(draft !== null && sheet.draftValue)}
            >
              Last {displayed} {unitLabel}
            </output>
          </label>
          <button
            type="button"
            aria-pressed={isAll}
            className={classes(sheet.allOption, isAll && sheet.allOptionActive)}
            onClick={toggleAll}
          >
            All
          </button>
        </div>
      </div>
    </section>
  );
}

const sheet = stylesheet({
  controls: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 18,
    flexWrap: 'wrap',
    minWidth: 0,
    $nest: {
      '@media (max-width: 980px)': {
        justifyContent: 'space-between',
      },
      '@media (max-width: 620px)': {
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: 12,
      },
    },
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  controlLabel: {
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  unitTabs: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 3,
    border: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
    borderRadius: 5,
    backgroundColor: ADALONG_COLORS.DARK_BLACK,
    maxWidth: '100%',
  },
  unitTab: {
    height: 34,
    padding: '0 11px',
    border: 0,
    borderRadius: 4,
    background: 'transparent',
    color: ADALONG_COLORS.LIGHT_GRAY,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    $nest: {
      '&:hover': {
        color: ADALONG_COLORS.SMOOTH_WHITE,
      },
      '&:focus-visible': {
        outline: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
        outlineOffset: -2,
      },
      '@media (max-width: 620px)': {
        flex: 1,
        minWidth: 0,
        padding: '0 8px',
      },
    },
  },
  unitTabActive: {
    backgroundColor: '#1f2fbf',
    color: ADALONG_COLORS.LIGHT_WHITE,
  },
  timeframeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 40,
    padding: '0 10px 0 14px',
    borderRadius: 5,
    backgroundColor: ADALONG_COLORS.DARK_BLACK,
    color: ADALONG_COLORS.SMOOTH_WHITE,
    $nest: {
      '@media (max-width: 620px)': {
        justifyContent: 'space-between',
      },
    },
  },
  rangeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 188,
    color: ADALONG_COLORS.SMOOTH_WHITE,
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    $nest: {
      '& input': {
        width: 112,
        accentColor: '#1f2fbf',
        cursor: 'pointer',
      },
      '@media (max-width: 620px)': {
        minWidth: 0,
        flex: 1,
      },
    },
  },
  allOption: {
    height: 34,
    padding: '0 11px',
    border: 0,
    borderRadius: 4,
    background: 'transparent',
    color: ADALONG_COLORS.LIGHT_GRAY,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    $nest: {
      '&:hover': {
        color: ADALONG_COLORS.SMOOTH_WHITE,
      },
      '&:focus-visible': {
        outline: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
        outlineOffset: 1,
      },
    },
  },
  allOptionActive: {
    backgroundColor: '#1f2fbf',
    color: ADALONG_COLORS.LIGHT_WHITE,
    $nest: {
      '&:hover': {
        color: ADALONG_COLORS.LIGHT_WHITE,
      },
    },
  },
  // While All is selected the range is muted visually but stays operable:
  // clicking the slider (or All again) reverts to a numeric timeframe.
  rangeMuted: {
    opacity: 0.45,
    $nest: {
      '& input': {
        cursor: 'pointer',
      },
    },
  },
  // A draft is shown while the slider is still held: it previews the value
  // that will commit on release, without being the active timeframe yet.
  draftValue: {
    opacity: 0.6,
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
});
